import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { activityView } from '../utils/activityView';
import { relativeTime } from '../utils/relativeTime';

const COLOR_DOT = {
  success: 'text-steam-success',
  warn:    'text-steam-warn',
  danger:  'text-steam-danger',
  muted:   'text-surface-muted'
};

const INPUT =
  'px-[10px] py-[7px] bg-surface-base text-surface-text border border-surface-border text-xs focus:outline-none focus:ring-2 focus:ring-accent rounded-[2px]';

const WINDOW_HOURS = { '24h': 24, '7d': 24 * 7, '30d': 24 * 30 };
function computeSinceIso(window) {
  const hours = WINDOW_HOURS[window];
  if (!hours) return null;
  return new Date(Date.now() - hours * 3600 * 1000).toISOString();
}

export default function ActivityPage() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [account, setAccount] = useState('');
  const [eventType, setEventType] = useState('all');
  const [timeWindow, setTimeWindow] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const nextBeforeRef = useRef(null);

  const fetchPage = useCallback(async (reset = false) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', '50');
      if (!reset && nextBeforeRef.current) params.set('before', nextBeforeRef.current);
      if (account.trim()) params.set('account', account.trim());
      if (eventType !== 'all') params.set('event', eventType);
      const sinceIso = computeSinceIso(timeWindow);
      if (sinceIso) params.set('since', sinceIso);
      const resp = await api.get(`/api/activity?${params.toString()}`);
      setEvents((prev) => reset ? resp.data.events : [...prev, ...resp.data.events]);
      setNextBefore(resp.data.nextBefore);
      nextBeforeRef.current = resp.data.nextBefore;
      setError('');
    } catch {
      setError('Failed to load activity.');
    } finally {
      setLoading(false);
    }
  }, [account, eventType, timeWindow]);

  useEffect(() => {
    setEvents([]);
    setNextBefore(null);
    nextBeforeRef.current = null;
    fetchPage(true);
  }, [account, eventType, timeWindow]);

  const openAccount = (steamId64) => {
    navigate(`/accounts?steamId64=${encodeURIComponent(steamId64)}`);
  };

  return (
    <div>
      <div className="mb-4">
        <h1 className="text-[22px] font-bold tracking-tight">Activity</h1>
        <div className="text-xs text-surface-muted">Recent bans and cooldown transitions, newest first.</div>
      </div>

      <div className="flex gap-2 mb-3">
        <input
          type="text"
          value={account}
          onChange={(e) => setAccount(e.target.value)}
          placeholder="Filter by SteamID64…"
          className={`flex-1 ${INPUT} font-mono`}
        />
        <select value={eventType} onChange={(e) => setEventType(e.target.value)} className={INPUT}>
          <option value="all">Event: All</option>
          <option value="ban">Ban only</option>
          <option value="cooldown">Cooldown only</option>
        </select>
        <select value={timeWindow} onChange={(e) => setTimeWindow(e.target.value)} className={INPUT}>
          <option value="all">Window: All time</option>
          <option value="24h">Last 24h</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
        </select>
      </div>

      {error && <div className="text-steam-danger text-sm mb-3">{error}</div>}

      <div className="bg-surface-panel border border-surface-border">
        {events.length === 0 && !loading && (
          <div className="p-10 text-center text-surface-muted text-sm">No activity matches these filters.</div>
        )}
        {events.map((ev, i) => {
          const v = activityView(ev);
          const { relative, absolute } = relativeTime(ev.changedAt || ev.detectedAt);
          return (
            <div
              key={`${v.timestampMs}-${i}`}
              className={`grid grid-cols-[120px_28px_minmax(0,1fr)_auto] gap-[10px] px-[14px] py-[10px] border-b border-surface-base items-center text-xs ${v.color === 'danger' ? 'bg-[rgba(217,65,38,0.04)]' : ''}`}
            >
              <div className="text-[10px] text-surface-muted font-mono" title={absolute}>{relative}</div>
              <div className={`text-center text-[14px] ${COLOR_DOT[v.color]}`}>●</div>
              <div className="min-w-0 truncate">
                <button
                  type="button"
                  onClick={() => openAccount(v.steamId64)}
                  className="text-accent font-semibold hover:underline"
                  title={ev.displayName ? `${ev.steamId64} · View on Accounts tab` : 'View this account on the Accounts tab'}
                >{ev.displayName || v.steamId64}</button>
                <span className="text-surface-text"> {v.description}</span>
              </div>
              <div className={`text-[10px] uppercase tracking-wider ${v.color === 'danger' ? 'text-steam-danger' : 'text-surface-muted'}`}>{v.tag}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 flex justify-center">
        {nextBefore ? (
          <button
            type="button"
            onClick={() => fetchPage(false)}
            disabled={loading}
            className="px-[14px] py-2 bg-transparent text-accent border border-surface-border text-[11px] font-semibold uppercase tracking-wider rounded-[2px] disabled:opacity-40"
          >{loading ? 'Loading…' : 'Load 50 more'}</button>
        ) : events.length > 0 && (
          <div className="text-[11px] text-surface-muted">End of activity.</div>
        )}
      </div>
    </div>
  );
}
